import { css } from '@emotion/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import FluentArrowSync24Regular from '~icons/fluent/arrow-sync-24-regular';

import {
	ButtonCancel,
	ButtonRow,
	ButtonWithLoading,
} from '@adflow/frontend/ui-buttons';
import { HubTooltip } from '@adflow/frontend/ui-common';
import { ModalWithLayout } from '@adflow/frontend/ui-modal';
import { theme } from '@adflow/frontend/ui-theme';
import { useAbility } from '@adflow/frontend/util-auth';

import {
	useSyncFacebookAccountsMutation,
	useSyncGMBAccountsMutation,
	useSyncGoogleAdsAccountsMutation,
	useSyncSalesforceAccountsMutation,
} from './resync-queries';

const styles = {
	resyncTitle: css({
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		fontSize: '24px',
	}),
	resyncContainer: css({
		display: 'flex',
		flexDirection: 'column',
		width: '500px',
		maxWidth: '500px',
		gap: '1rem',
	}),
	resyncIconContainer: (missingPermission?: boolean) =>
		css({
			paddingLeft: '0.75rem',
			paddingRight: '0.75rem',
			display: 'flex',
			alignItems: 'center',
			cursor: 'pointer',
			...(missingPermission && {
				cursor: 'not-allowed',
				color: theme.colors.text.disabled,
			}),
		}),
	resyncIcon: css({
		width: '1.25em',
		height: '1.25em',
	}),
};

export function ResyncAccounts() {
	const { t } = useTranslation([
		'common',
		'facebook',
		'google-ads',
		'salesforce-import',
	]);
	const [show, setShow] = useState(false);

	const { ability, missingPermissionText } = useAbility();

	const syncGoogleAdsMutation = useSyncGoogleAdsAccountsMutation();
	const syncFacebookMutation = useSyncFacebookAccountsMutation();
	const syncSalesforceMutation = useSyncSalesforceAccountsMutation();
	const syncGMBMutation = useSyncGMBAccountsMutation();

	const onClose = () => {
		setShow(false);
	};

	const handleSyncGoogleAds = async () => {
		await syncGoogleAdsMutation.mutateAsync();
	};

	const handleSyncFacebook = async () => {
		await syncFacebookMutation.mutateAsync();
	};

	const handleSyncSalesforce = async () => {
		await syncSalesforceMutation.mutateAsync();
	};

	const handleSyncGMB = async () => {
		await syncGMBMutation.mutateAsync();
	};

	const missingPermission = ability.cannot('create', 'Account Sync');

	const resyncButtons = [
		{
			id: 'google-ads',
			title: t('google-ads:Google Ads'),
			description: t('common:sync.syncGoogleAdsButton'),
			onClick: handleSyncGoogleAds,
			loading: syncGoogleAdsMutation.isPending,
		},
		{
			id: 'facebook',
			title: t('facebook:facebook'),
			description: t('common:sync.syncFacebookButton'),
			onClick: handleSyncFacebook,
			loading: syncFacebookMutation.isPending,
		},
		{
			id: 'salesforce',
			title: t('salesforce-import:Salesforce'),
			description: t('common:sync.syncSalesforceButton'),
			onClick: handleSyncSalesforce,
			loading: syncSalesforceMutation.isPending,
		},
		{
			id: 'gmb',
			title: t('google-ads:Google My Business'),
			description: t('common:sync.syncGMBButton'),
			onClick: handleSyncGMB,
			loading: syncGMBMutation.isPending,
		},
	];

	return (
		<>
			<HubTooltip
				title={
					missingPermission
						? missingPermissionText('create', 'Account Sync')
						: t('common:sync.resyncAccounts')
				}
			>
				<div
					onClick={() => {
						if (missingPermission) return;
						setShow(true);
					}}
					css={styles.resyncIconContainer(missingPermission)}
				>
					<FluentArrowSync24Regular css={styles.resyncIcon} />
				</div>
			</HubTooltip>
			<ModalWithLayout
				show={show}
				header={
					<h1 css={styles.resyncTitle}>{t('common:sync.resyncAccounts')}</h1>
				}
				body={
					<div css={styles.resyncContainer}>
						{resyncButtons.map((button) => (
							<ButtonWithLoading
								key={button.id}
								loading={button.loading}
								onClick={button.onClick}
								variant="outlined"
							>
								{button.description}
							</ButtonWithLoading>
						))}
					</div>
				}
				footer={
					<ButtonRow
						right={
							<ButtonCancel onClick={onClose}>{t('common:Close')}</ButtonCancel>
						}
					/>
				}
			/>
		</>
	);
}
